import { useStore } from '@nanostores/react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { DeleteTeamDialog } from './DeleteTeamDialog';
import { TeamName } from './TeamName';
import { TeamProjectVisibility } from './TeamProjectVisibility';
import { ContainerWithBackButton } from '~/components/dialogs/settings/ContainerWithBackButton';
import { DangerZoneWrapper } from '~/components/dialogs/settings/DangerZone';
import { Button } from '~/components/ui/Button';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { Switch } from '~/components/ui/Switch';
import { getTeamSettings, updateTeamSettings } from '~/lib/.client/api/organizations';
import { userStore } from '~/lib/stores/user';
import type { OrganizationSettings } from '~/types/organization';

interface TeamSettingsProps {
  closeTeamSettings: VoidFunction;
}

export const TeamSettings = memo(({ closeTeamSettings }: TeamSettingsProps) => {
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const [teamSettings, setTeamSettings] = useState<OrganizationSettings | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [settingsError, setSettingsError] = useState(false);
  const [settingsFetchTrigger, setSettingsFetchTrigger] = useState(0);
  const orgKey = `${activeOrganization?.provider}_${activeOrganization?.slug}`;

  useEffect(() => {
    if (!activeOrganization) {
      return;
    }

    getTeamSettings({ organization: activeOrganization })
      .then((settings) => {
        setTeamSettings(settings);
      })
      .catch((error) => {
        setSettingsError(true);
        toast.error(error.message ? `Failed to load team settings: ${error.message}` : 'Failed to load team settings');
      });
  }, [orgKey, settingsFetchTrigger]);

  const updateSetting = useCallback(
    async (settingName: keyof OrganizationSettings, value: any) => {
      if (!activeOrganization || !teamSettings) {
        return;
      }

      const oldValue = teamSettings[settingName];

      setTeamSettings({
        ...teamSettings,
        [settingName]: value,
      });

      try {
        const updatedSettings = await updateTeamSettings({
          organization: activeOrganization,
          settings: { [settingName]: value },
        });

        if (!updatedSettings) {
          throw new Error('Failed to update team settings');
        }

        userStore.patchOrganizationSettings(activeOrganization, updatedSettings);
      } catch {
        setTeamSettings({
          ...teamSettings,
          [settingName]: oldValue,
        });
        toast.error(`Failed to update ${settingName}. Changes have been reverted.`);
      }
    },
    [activeOrganization, teamSettings],
  );

  const handleSettingsFetch = useCallback(() => {
    setSettingsError(false);
    setSettingsFetchTrigger((v) => v + 1);
  }, []);

  const switches = useMemo(() => {
    if (!teamSettings) {
      return [];
    }

    const handleSwitchIntegrations = (value: boolean) => {
      updateSetting('showIntegrations', !value);
    };

    const handleSwitchAccountRestriction = (value: boolean) => {
      updateSetting('showAccountSelector', !value);
    };

    return [
      <SwitchItem
        key="integrations"
        label="Restrict project integrations"
        description="When enabled, only the GitHub integration will be available. All other integrations (Stripe, Supabase, etc.) will be hidden and blocked."
        checked={!teamSettings.showIntegrations}
        onChange={handleSwitchIntegrations}
      />,
      <SwitchItem
        key="account-restriction"
        label="Personal account restriction"
        description="When activated, members of this team workspace will not be able to use their personal workspace, nor create other teams."
        checked={!teamSettings.showAccountSelector}
        onChange={handleSwitchAccountRestriction}
      />,
    ];
  }, [teamSettings?.showIntegrations, teamSettings?.showAccountSelector, updateSetting]);

  if (settingsError) {
    return (
      <ContainerWithBackButton title="Team settings" className="items-center gap-4 mt-1/4" onClose={closeTeamSettings}>
        <div className="flex flex-col items-center gap-1 text-center text-bolt-ds-textTertiary">
          <p>We were unable to get your team's settings.</p>
          <p>Please try again</p>
        </div>
        <Button className="" variant="primary" onClick={handleSettingsFetch}>
          Retry
        </Button>
      </ContainerWithBackButton>
    );
  }

  if (!activeOrganization || !teamSettings) {
    return (
      <ContainerWithBackButton title="Team settings" className="items-center gap-4 mt-1/4" onClose={closeTeamSettings}>
        <div className="flex items-center justify-center h-32 text-bolt-ds-textTertiary text-center">
          <LoadingDots text="Loading team settings" />
        </div>
      </ContainerWithBackButton>
    );
  }

  return (
    <ContainerWithBackButton title="Team settings" onClose={closeTeamSettings}>
      <section className="flex flex-col gap-4">
        <h3 className="font-semibold text-sm text-bolt-elements-textSecondary">General</h3>
        <div className="flex flex-col gap-6">
          <TeamName organization={activeOrganization} />
          <TeamProjectVisibility maxVisibility={teamSettings.maxVisibility} updateSetting={updateSetting} />

          {switches}
        </div>
      </section>

      <DangerZoneWrapper>
        <div className="flex flex-col text-sm gap-1.5">
          <p className="font-semibold">Delete team</p>
          <p className="text-sm max-w-[85%] text-bolt-elements-textSecondary">
            Permanently deletes the entire team and all projects created within it. This action cannot be undone and
            will affect all members.
          </p>
        </div>
        <Button color="danger" variant="tint" className="w-min" onClick={() => setIsDeleteDialogOpen(true)}>
          Delete team
        </Button>

        <DeleteTeamDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          org={activeOrganization}
        />
      </DangerZoneWrapper>
    </ContainerWithBackButton>
  );
});

interface SwitchItemProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

const SwitchItem = ({ label, description, checked, onChange }: SwitchItemProps) => {
  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col text-sm gap-1.5">
        <p className="font-semibold">{label}</p>
        <p className="text-sm max-w-[85%] text-bolt-elements-textSecondary">{description}</p>
      </div>
      <Switch className="shrink-0" size="sm" checked={checked} onCheckedChange={onChange} />
    </div>
  );
};
